import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Head from 'next/head';
import { useTranslation } from 'next-i18next';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/router';
import cx from 'classnames';

import { Icon, IconButton, Link } from 'components';
import { getLayout } from 'components/layouts/Header';
import { Page } from 'components/layouts';
import { ButtonV3 } from 'components/ComponentV2';
import { Form as FormComponent } from 'components/form';
import { SearchingSkeleton } from 'components/skeletons/AiSearch.component';
import { RecentSearches } from 'components/ai/search';
import WalkthroughTooltip, { WalkthroughTooltipType } from 'components/WalkthroughTooltip';
import { useResponsive } from 'hooks/useResponsive';
import { fetchTranslationFiles, LOCALE_PAGES } from 'lib/services/page';
import { PAGE_ROUTES } from 'lib/page-routes';
import { apiInstance } from 'lib/utils/axios';
import { useAuth } from 'lib/providers/AuthProvider';
import { TSearchQuery } from 'lib/models/search-query';
import { API_ROUTES } from 'lib/api-routes';
import { useApp } from 'lib/contexts/save-in-app-context';
import { TAISearch } from 'lib/models/ai-search';

import styles from './AISearch.module.scss';

const AISearch: React.FC = () => {
  const { t } = useTranslation('ai.search');
  const { user } = useAuth();
  const router = useRouter();
  const screens = useResponsive();
  const { query } = router;
  const searchQueryString = (query.q as string) || '';
  const { mutateAISearchesResponse, aiSearches, isAISearchLoading } = useApp();
  const controllerRef = useRef(new AbortController());
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const [closeWalkthroughTooltip, setCloseWalkthroughTooltip] = useState(false);
  const [isWalkthroughOpen, setIsWalkthroughOpen] = useState(false);

  useEffect(() => {
    router.prefetch(PAGE_ROUTES.AI_SEARCH_RESULTS);
  }, []);

  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm({ defaultValues: { search: searchQueryString } });

  const onSubmitForm = useCallback(
    async (data: { search: string }) => {
      const { search } = data;
      if (!search.trim()) return;
      const controller = controllerRef.current;

      try {
        const { data: searchQuery } = await apiInstance.post<TSearchQuery>(
          API_ROUTES.CANDIDATE_SEARCHES_QUERIES,
          {
            query: search,
          },
          {
            signal: controller.signal,
          }
        );
        const aiSearch: TAISearch = { id: searchQuery.ai_search_id, name: searchQuery.input, search_queries: [searchQuery], type: 'ai_search' };
        mutateAISearchesResponse(
          (prevAISearchResponse) => {
            if (!prevAISearchResponse) return prevAISearchResponse;
            return {
              ...prevAISearchResponse,
              data: [aiSearch, ...prevAISearchResponse.data],
            };
          },
          { revalidate: false }
        );
        const aiSearchPageRoute = PAGE_ROUTES.AI_SEARCH_RESULTS.replace('[searchId]', searchQuery.ai_search_id);
        await router.push(aiSearchPageRoute);
      } catch (e) {
        console.error(e);
      }
    },
    [controllerRef]
  );

  // Search on land when user comes from search results page after searching
  useEffect(() => {
    if (searchQueryString.trim()) {
      const { q: _, ...rest } = query;
      router.replace(
        {
          query: rest,
        },
        undefined,
        { shallow: true }
      );
      submitButtonRef.current?.click();
    }
  }, []);

  const handleCancel = useCallback(() => {
    const controller = controllerRef.current;
    controller.abort();
    controllerRef.current = new AbortController();
  }, [controllerRef]);

  const handleKeyPress: React.KeyboardEventHandler<HTMLTextAreaElement> = useCallback(
    (event) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        submitButtonRef.current?.click();
      } else if (isWalkthroughOpen) setCloseWalkthroughTooltip(true);
    },
    [submitButtonRef, isWalkthroughOpen]
  );

  const randomPlaceholder = useMemo(() => {
    return Math.floor(Math.random() * 10) + 1;
  }, []);

  return (
    <>
      <Head>
        <title>AI Search</title>
        <meta name="description" content="AI Search" />
      </Head>
      <Page className={styles.page}>
        <section className={styles.container}>
          <main className={cx(styles.content, { [styles['no-searches']]: !isAISearchLoading && aiSearches.length === 0 })}>
            {!isSubmitting && (
              <div className={styles.header}>
                <p className={styles.salutation}>{t('search.salutation', { name: user?.first_name })}</p>
                <p className={styles.question}>{t('search.question')}</p>
              </div>
            )}
            <FormComponent className={styles['search-container']} onSubmit={handleSubmit(onSubmitForm)}>
              <Icon iconName="icon_search" className={styles['search-icon']} size="mediumplus" />
              <WalkthroughTooltip
                walkthroughType={WalkthroughTooltipType.AI_SEARCH_SEARCH_INPUT}
                title={t('walkthrough.title')}
                description={t('walkthrough.description')}
                buttonOneText={t('walkthrough.button')}
                containerClassName={styles['walkthrough-wrapper']}
                className={cx(styles['walkthrough-container'], {
                  [styles['two-line-placeholder']]: randomPlaceholder > 8,
                })}
                closeWalkthrough={closeWalkthroughTooltip}
                setIsWalkthroughOpen={setIsWalkthroughOpen}
              >
                <textarea
                  autoFocus
                  className={styles['search-box']}
                  rows={screens.sm ? 4 : 3}
                  placeholder={t(`placeholders.placeholder-${randomPlaceholder}`)}
                  {...register('search')}
                  onKeyPress={handleKeyPress}
                />
              </WalkthroughTooltip>
              {isSubmitting ? (
                <button className={styles['cancel-button']} onClick={handleCancel}>
                  <span></span>
                </button>
              ) : (
                <IconButton iconName="icon_arrow-up-2" type="submit" className={styles['submit-button']} ref={submitButtonRef} />
              )}
            </FormComponent>
            {isSubmitting && <SearchingSkeleton className={styles['searching-skeleton']} />}
            {!isSubmitting && <RecentSearches />}
          </main>
          {!isSubmitting && (
            <footer className={styles.footer}>
              <div className={styles['title-bar']}>
                <div className={styles.divider}></div>
                <h3 className={styles.title}>{t('footer.title')}</h3>
                <div className={styles.divider}></div>
              </div>
              <div className={styles.categories}>
                <Link href={PAGE_ROUTES.JOBS_SEARCH} underlineOnHover={false}>
                  <ButtonV3 color="secondary" borderRadius={1000} startIcon="icon_briefcase_filled" startIconClassName={styles.jobs}>
                    {t('footer.search-categories.0')}
                  </ButtonV3>
                </Link>
                <Link href={PAGE_ROUTES.INTERNSHIPS_SEARCH} underlineOnHover={false}>
                  <ButtonV3 color="secondary" borderRadius={1000} startIcon="icon_spaceship-filled" startIconClassName={styles.internships}>
                    {t('footer.search-categories.1')}
                  </ButtonV3>
                </Link>
                <Link href={PAGE_ROUTES.EVENTS_SEARCH} underlineOnHover={false}>
                  <ButtonV3 color="secondary" borderRadius={1000} startIcon="icon_party_filled" startIconClassName={styles.events}>
                    {t('footer.search-categories.2')}
                  </ButtonV3>
                </Link>
                <Link href={PAGE_ROUTES.COMPANIES_SEARCH} underlineOnHover={false}>
                  <ButtonV3 color="secondary" borderRadius={1000} startIcon="icon_office-filled" startIconClassName={styles.companies}>
                    {t('footer.search-categories.3')}
                  </ButtonV3>
                </Link>
                <Link href={PAGE_ROUTES.SOCIETIES_SEARCH} underlineOnHover={false}>
                  <ButtonV3 color="secondary" borderRadius={1000} startIcon="icon_multiple-11_filled" startIconClassName={styles.communities}>
                    {t('footer.search-categories.4')}
                  </ButtonV3>
                </Link>
              </div>
            </footer>
          )}
        </section>
      </Page>
    </>
  );
};

export async function getServerSideProps(context: { locale: string }) {
  const { locale } = context;

  return {
    props: {
      ...(await fetchTranslationFiles({
        locale,
        pages: [LOCALE_PAGES.AI_SEARCH],
      })),
    },
  };
}

// @ts-ignore
AISearch.getLayout = getLayout;

export default AISearch;
